import { FormEvent, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'

// local imports & assets
import parkingIcon from 'assets/icons/park-area.svg'
import DateUtil from 'utils/DateUtil'
import { licenceRegex, toInputUppercase } from 'utils/Miscellaneous'
import ParkAreaService from 'service/ParkAreaService'
import { IFilteredParkAreaList } from 'types/ParkAreaType'
import { ActionArea, PlateInputText, PlateModalHeader, ResButton, ResContentArea } from './styled'

/* Types & Constants */
interface IModalFooterProps {
  disabled: boolean
  loading: boolean
  onSave: () => void
}

interface IAppointmentModalProps {
  filterDate: Date
  selectedParkArea: IFilteredParkAreaList
  open: boolean
  closeModal: () => void
  showNotification: () => void
}

export default function ParkReservationModal({
  filterDate: dateFromList,
  selectedParkArea,
  open,
  closeModal,
  showNotification,
}: IAppointmentModalProps) {
  const { t } = useTranslation()
  const [plate, setPlate] = useState('')
  const [valid, setValid] = useState(true)
  const [loading, setLoading] = useState(false)
  const dateStr = dateFromList.toISOString()

  const handleInputChange = (e: FormEvent<HTMLInputElement>) => {
    const isValid = licenceRegex.test(e.currentTarget.value)
    setValid(isValid)
    setPlate(e.currentTarget.value)
  }

  /* Complete Reservation */
  const handleSaveReservation = () => {
    const body = {
      id: 0,
      plate: plate,
      reservationStartTime: DateUtil.formatStringToDateTime(dateStr),
      reservationEndTime: DateUtil.formatStringToDateTime(dateStr),
      parkAreaId: selectedParkArea.id,
    }

    setLoading(true)
    ParkAreaService.saveParkAreaReservation(body)
      .then(() => {
        showNotification()
        closeModal()
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false))
  }

  /* Close with cancel */
  const handleCloseOnCancel = () => closeModal()

  return (
    <Dialog
      blockScroll
      keepInViewport
      focusOnShow={false}
      closeOnEscape={false}
      closable={!loading ? true : false}
      header={
        <PlateModalHeader>
          <img src={parkingIcon} width={60} height={60} alt="park-area-reservation" />
        </PlateModalHeader>
      }
      draggable={false}
      resizable={false}
      visible={open}
      style={{ width: '500px' }}
      breakpoints={{ '1536px': '400px', '1280px': '400px' }}
      onHide={handleCloseOnCancel}
      footer={<FooterComponent onSave={handleSaveReservation} disabled={!valid || !plate} loading={loading} />}>
      <ResContentArea>
        <span className="header">{t('park-area-res-modal.write-plate')}</span>

        <div className="input">
          <PlateInputText
            name="plate"
            keyfilter="alphanum"
            value={plate}
            onChange={handleInputChange}
            onInput={toInputUppercase}
            className={valid ? '' : 'p-invalid'}
            maxLength={8}
            spellCheck={false}
            autoFocus
          />
          <div style={{ height: '24px' }}>
            <small className="text-danger fw-semibold">{valid ? '' : t('park-area-res-modal.valid-plate-msg')}</small>
          </div>
        </div>

        <div className="info-area">
          <div className="info">
            <i className="pi pi-info-circle" style={{ color: '#45BFE5', fontSize: '1rem' }}></i>
            <span className="park-info-text">{t('park-area-res-modal.modal-info')}</span>
          </div>

          {!!selectedParkArea?.plateExplanation && (
            <div className="info">
              <i className="pi pi-info-circle" style={{ color: '#45BFE5', fontSize: '1rem' }}></i>
              <span className="park-info-text">{selectedParkArea?.plateExplanation || ''}</span>
            </div>
          )}
        </div>
      </ResContentArea>
    </Dialog>
  )
}

function FooterComponent({ onSave, disabled, loading }: IModalFooterProps) {
  const { t } = useTranslation()
  return (
    <ActionArea>
      <ResButton
        loading={loading}
        id="openSidebar"
        disabled={disabled}
        label={t('buttons.book-reservation')}
        onClick={onSave}
      />
    </ActionArea>
  )
}
